import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  EstabelecimentoDataService,
  ServicoService,
  NichoTermosService,
} from '@nomoh-monorepo/data-access-bardo-administrador';

import { FormFieldComponent } from '@nomoh-monorepo/shared-common-ui';
import { IServico } from '@nomoh-monorepo/shared-types';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';

import { TranslateModule } from '@ngx-translate/core';
import { I18nService } from '@nomoh-monorepo/data-access-shared-i18n';

@Component({
  selector: 'lib-servico-criar-editar',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    FormFieldComponent,
    InputNumberModule,
    InputGroupModule,
    InputGroupAddonModule,
    DatePickerModule,
    TranslateModule,
  ],
  templateUrl: './servico-criar-editar.component.html',
  styleUrls: ['./servico-criar-editar.component.scss'],
})
export class ServicoCriarEditarComponent implements OnInit {
  private fb = inject(FormBuilder);
  private servicoService = inject(ServicoService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private i18n = inject(I18nService);
  $nicho = inject(NichoTermosService).$nicho;
  simboloMoeda = this.i18n.$simboloMoeda;


  private readonly DURACAO_MINIMA_MINUTOS = 30;

  @Input() servico?: IServico | null;
  @Input() tipoServicoId?: string | null;
  @Output() salvo = new EventEmitter<void>();
  @Output() cancelar = new EventEmitter<void>();
  @Output() loadingChange = new EventEmitter<boolean>();

  $loading = signal<boolean>(false);

  form: FormGroup = {} as FormGroup;

  duracaoMaximaDate = computed(() => this.calcularDuracaoMaximaEstabelecimento());

  duracaoMaximaMinutos = computed(() => {
    const maxDate = this.duracaoMaximaDate();
    if (!maxDate) return 720;
    return this.converterDuracaoParaMinutos(maxDate);
  });

  get $modoEdicao(): boolean {
    return !!this.servico?.id;
  }

  ngOnInit(): void {
    this.initForm();
    this.preencherFormulario();
  }

  initForm(): void {
    this.form = this.fb.group({
      nome: [
        '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(50)],
      ],
      duracaoTime: [
        this.criarDuracaoInicial(),
        [
          Validators.required,
          this.validarDuracaoMinima,
          this.validarDuracaoMaxima.bind(this),
        ],
      ],
      valor: [null, [Validators.min(0)]],
    });
  }

  private preencherFormulario(): void {
    if (!this.servico) {
      return;
    }

    const duracaoMinutos =
      typeof this.servico.duracao === 'string'
        ? parseInt(this.servico.duracao, 10)
        : this.servico.duracao ?? this.DURACAO_MINIMA_MINUTOS;

    this.form.patchValue({
      nome: this.servico.nome,
      duracaoTime: this.criarDateDuracao(duracaoMinutos),
      valor: this.servico.valor,
    });
  }

  private criarDuracaoInicial(): Date {
    return this.criarDateDuracao(this.DURACAO_MINIMA_MINUTOS);
  }

  private criarDateDuracao(minutos: number): Date {
    const base = new Date();
    base.setHours(Math.floor(minutos / 60), minutos % 60, 0, 0);
    return base;
  }

  private validarDuracaoMinima = (control: AbstractControl) => {
    const minutos = this.converterDuracaoParaMinutos(
      control.value as Date | string | null
    );
    if (minutos < this.DURACAO_MINIMA_MINUTOS) {
      return { duracaoMinima: true };
    }
    return null;
  };

  private converterDuracaoParaMinutos(valor?: Date | string | null): number {
    if (!valor) {
      return this.DURACAO_MINIMA_MINUTOS;
    }
    if (typeof valor === 'string') {
      const partes = valor.split(':');
      if (partes.length >= 2) {
        const h = Number(partes[0]);
        const m = Number(partes[1]);
        if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m;
      }
      return this.DURACAO_MINIMA_MINUTOS;
    }
    const horas = valor.getHours();
    const minutos = valor.getMinutes();
    return horas * 60 + minutos;
  }

  private parseHorario(horario: string): number {
    if (!horario || typeof horario !== 'string') {
      return 0;
    }

    const partes = horario.split(':');
    if (partes.length < 2) {
      return 0;
    }

    const h = Number(partes[0]);
    const m = Number(partes[1]);

    if (!Number.isFinite(h) || !Number.isFinite(m)) {
      return 0;
    }

    return h * 60 + m;
  }

  private calcularDuracaoMaximaEstabelecimento(): Date | null {
    const estabelecimento = this.estabelecimentoDataService.$estabelecimentoAtual();

    if (!estabelecimento?.horaAbertura || !estabelecimento?.horaFechamento) {
      const base = new Date();
      base.setHours(12, 0, 0, 0);
      return base;
    }

    const inicioMinutos = this.parseHorario(estabelecimento.horaAbertura);
    const fimMinutos = this.parseHorario(estabelecimento.horaFechamento);

    let duracaoMinutos = fimMinutos - inicioMinutos;

    if (duracaoMinutos <= 0) {
      duracaoMinutos = 1440;
    }

    if (duracaoMinutos > 1440) {
      duracaoMinutos = 1440;
    }

    return this.criarDateDuracao(duracaoMinutos);
  }

  private validarDuracaoMaxima = (control: AbstractControl) => {
    const valor = control.value;
    if (!valor) {
      return null;
    }

    const duracaoSelecionada = this.converterDuracaoParaMinutos(valor);
    const duracaoMaxima = this.duracaoMaximaMinutos();

    if (duracaoSelecionada > duracaoMaxima) {
      return {
        duracaoMaxima: {
          maxMinutos: duracaoMaxima,
          atualMinutos: duracaoSelecionada,
        },
      };
    }

    return null;
  };

  formatarDuracaoExibicao(minutos: number): string {
    if (minutos <= 0) {
      return '0min';
    }

    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;

    if (horas === 0) {
      return `${mins}min`;
    }

    if (mins === 0) {
      return `${horas}h`;
    }

    return `${horas}h ${mins}min`;
  }

  async salvar(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const duracaoSelecionada: Date | string | null = this.form.value.duracaoTime;
    const duracaoMinutos = this.converterDuracaoParaMinutos(duracaoSelecionada);

    if (duracaoMinutos < this.DURACAO_MINIMA_MINUTOS) {
      this.form.get('duracaoTime')?.setErrors({ duracaoMinima: true });
      return;
    }

    this.$loading.set(true);
    this.loadingChange.emit(true);

    const valorForm = this.form.value.valor;
    const valorFinal = (valorForm !== null && valorForm !== undefined && valorForm !== '') ? Number(valorForm) : 0;

    try {
      if (this.$modoEdicao && this.servico) {
        await this.servicoService.atualizar(this.servico.id, {
          nome: this.form.value.nome?.trim(),
          duracao: duracaoMinutos,
          valor: valorFinal,
        });
      } else {
        const estabelecimentoId =
          this.estabelecimentoDataService.$estabelecimentoAtual()?.id;
        if (!estabelecimentoId) {
          return;
        }

        await this.servicoService.criar({
          nome: this.form.value.nome?.trim(),
          tipoServicoId: this.tipoServicoId ?? undefined,
          duracao: duracaoMinutos,
          valor: valorFinal,
          estabelecimentoId,
          status: true,
        });
      }

      this.salvo.emit();
    } catch {
      // erro tratado no service
    } finally {
      this.$loading.set(false);
      this.loadingChange.emit(false);
    }
  }
}
